import React, { useState, useEffect } from 'react';
import { Link2, Plus, X, Ticket as TicketIcon } from 'lucide-react';
import { TicketsService } from '@/services';
import { TicketRelacionado, Ticket as TicketType } from '@/types';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { useDarkMode } from '@/contexts/DarkModeContext';
import ConfirmationModal from '@/components/ConfirmationModal';

interface RelatedTicketsProps {
  ticketId: number;
  readOnly?: boolean;
}

export default function RelatedTickets({ ticketId, readOnly = false }: RelatedTicketsProps) {
  const [relacionamentos, setRelacionamentos] = useState<TicketRelacionado[]>([]);
  const [showAdicionar, setShowAdicionar] = useState(false);
  const [ticketIdRelacionado, setTicketIdRelacionado] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [relacionamentoToRemove, setRelacionamentoToRemove] = useState<number | null>(null);
  const navigate = useNavigate();
  const { isDarkMode } = useDarkMode();

  useEffect(() => {
    loadRelacionamentos();
  }, [ticketId]);

  const loadRelacionamentos = async () => {
    try {
      const data = await TicketsService.getTicketsRelacionados(ticketId);
      setRelacionamentos(data);
    } catch (error: any) {
      console.error('Erro ao carregar tickets relacionados:', error);
    }
  };

  const handleVincular = async () => {
    if (!ticketIdRelacionado) {
      toast.error('Informe o ID do ticket relacionado');
      return;
    }

    const idRelacionado = Number(ticketIdRelacionado);
    if (isNaN(idRelacionado) || idRelacionado === ticketId) {
      toast.error('ID inválido ou igual ao ticket atual');
      return;
    }

    try {
      setIsLoading(true);
      await TicketsService.vincularTicket(ticketId, idRelacionado, 'relacionado');
      await loadRelacionamentos();
      setShowAdicionar(false);
      setTicketIdRelacionado('');
      toast.success('Ticket vinculado com sucesso');
    } catch (error: any) {
      console.error('Erro ao vincular ticket:', error);
      toast.error(error.response?.data?.error || 'Erro ao vincular ticket');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDesvincularClick = (relacionamentoId: number) => {
    setRelacionamentoToRemove(relacionamentoId);
    setShowConfirmModal(true);
  };

  const handleDesvincular = async () => {
    if (!relacionamentoToRemove) return;

    try {
      setIsLoading(true);
      await TicketsService.desvincularTicket(ticketId, relacionamentoToRemove);
      await loadRelacionamentos();
      toast.success('Ticket desvinculado com sucesso');
      setShowConfirmModal(false);
      setRelacionamentoToRemove(null);
    } catch (error: any) {
      console.error('Erro ao desvincular ticket:', error);
      toast.error(error.response?.data?.error || 'Erro ao desvincular ticket');
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusColor = (statusId?: number) => {
    switch (statusId) {
      case 1:
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 2:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
      case 3:
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Link2 className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          <h3 className="font-semibold text-gray-900 dark:text-gray-100">
            Tickets Relacionados
          </h3>
        </div>
        {!readOnly && (
          <button
            onClick={() => setShowAdicionar(!showAdicionar)}
            className="inline-flex items-center justify-center p-2 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            title="Vincular ticket relacionado"
          >
            <Plus className="w-4 h-4" />
          </button>
        )}
      </div>

      {showAdicionar && (
        <div className="w-full p-3 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-lg border border-gray-200/50 dark:border-gray-700/50 space-y-3">
          <input
            type="number"
            placeholder="ID do ticket relacionado"
            value={ticketIdRelacionado}
            onChange={(e) => setTicketIdRelacionado(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 text-sm focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400"
            disabled={isLoading}
          />
          <div className="flex gap-2">
            <button
              onClick={handleVincular}
              disabled={!ticketIdRelacionado || isLoading}
              className="flex-1 px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
            >
              {isLoading ? 'Vinculando...' : 'Vincular'}
            </button>
            <button
              onClick={() => {
                setShowAdicionar(false);
                setTicketIdRelacionado('');
              }}
              className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors text-sm font-medium"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      {relacionamentos.length === 0 && !showAdicionar && (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Nenhum ticket relacionado
        </p>
      )}
      {relacionamentos.length > 0 && (
        <div className="space-y-2">
          {relacionamentos.map((rel) => {
            const ticketRelacionadoId =
              rel.ticket_id === ticketId
                ? rel.ticket_relacionado_id
                : rel.ticket_id;
            const assunto =
              rel.ticket_id === ticketId
                ? rel.ticket_relacionado_assunto
                : rel.ticket_assunto;
            const statusId =
              rel.ticket_id === ticketId
                ? rel.ticket_relacionado_status_id
                : rel.ticket_status_id;

            return (
              <div
                key={rel.id}
                className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <div className="flex items-center gap-2 flex-1">
                  <TicketIcon className="w-3.5 h-3.5 text-gray-500 dark:text-gray-400 flex-shrink-0" />
                  <div
                    onClick={() => navigate(`/tickets/${ticketRelacionadoId}`)}
                    className="text-left flex-1 cursor-pointer"
                  >
                    <div className="flex items-center gap-2 flex-wrap">
                      <span 
                        className="text-xs font-medium hover:text-blue-600 dark:hover:text-blue-400"
                        style={{ 
                          color: isDarkMode ? 'rgb(255, 255, 255)' : 'rgb(17, 24, 39)'
                        }}
                      >
                        #{ticketRelacionadoId}
                      </span>
                      {assunto && (
                        <span 
                          className="text-xs hover:text-blue-600 dark:hover:text-blue-400"
                          style={{ 
                            color: isDarkMode ? 'rgb(209, 213, 219)' : 'rgb(75, 85, 99)'
                          }}
                        >
                          {assunto}
                        </span>
                      )}
                      {statusId && (statusId === 1 || statusId === 2 || statusId === 3) && (
                        <span
                          className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${getStatusColor(statusId)}`}
                        >
                          {statusId === 1
                            ? 'Aberto'
                            : statusId === 2
                            ? 'Fechado'
                            : 'Em Andamento'}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                {!readOnly && (
                  <button
                    onClick={() => handleDesvincularClick(rel.id)}
                    disabled={isLoading}
                    className="p-1 hover:bg-red-100 dark:hover:bg-red-900/20 rounded transition-colors flex-shrink-0"
                    title="Desvincular ticket"
                  >
                    <X className="w-3.5 h-3.5 text-red-600 dark:text-red-400" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Modal de Confirmação de Desvincular */}
      <ConfirmationModal
        isOpen={showConfirmModal}
        onClose={() => {
          if (!isLoading) {
            setShowConfirmModal(false);
            setRelacionamentoToRemove(null);
          }
        }}
        onConfirm={handleDesvincular}
        title="Desvincular Ticket"
        message="Deseja realmente desvincular este ticket?"
        confirmText="Desvincular"
        cancelText="Cancelar"
        type="warning"
        isLoading={isLoading}
      />
    </div>
  );
}

